import { AddressModel, PartnerModel } from '../court/court'
import { Language } from '../language'
import { FormField } from '../formData'
import { RoleType } from '../../utils/jwtUtils'
import { ThemeType } from '../contexts/AppContext'

export type UserProfile = {
  id: number
  firstName: string
  lastName: string
  nickname: string
  phoneNumber: string
  language: Language
  theme: ThemeType
  emailNotifications: boolean
  pushNotifications: boolean
  marketingNotifications: boolean
  skillLevel: SkillLevel | null
  dominantHand: DominantHand | null
  termsConsent: boolean
  emailConfirmed: boolean
  address: AddressModel | null
  partners?: PartnerModel[]
}

export type ProfileMetadata = {
  id: string
  userId: string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  roleTypeList: RoleType[]
  joinedAt: string
  status: UserStatus
  locked: boolean
  lockReason?: UserLockReason
}

export enum UserStatus {
  REGISTERED = 'REGISTERED',
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
  MANAGED = 'MANAGED',
}

export enum UserLockReason {
  NO_SHOW = 'NO_SHOW',
  PAYMENT_ISSUE = 'PAYMENT_ISSUE',
  ABUSE = 'ABUSE',
  OTHER = 'OTHER',
  FAILED_LOGIN_ATTEMPTS = 'FAILED_LOGIN_ATTEMPTS',
}

// Reasons an admin can pick; FAILED_LOGIN_ATTEMPTS is set by the system only.
export const ADMIN_LOCK_REASONS = [
  UserLockReason.NO_SHOW,
  UserLockReason.PAYMENT_ISSUE,
  UserLockReason.ABUSE,
  UserLockReason.OTHER,
] as const

export enum AccountActionType {
  LOCK = 'LOCK',
  UNLOCK = 'UNLOCK',
  RESET_PASSWORD = 'RESET_PASSWORD',
}

export type AccountActionRequest = {
  type: AccountActionType
  reason?: UserLockReason
  details?: string
}

export type UserRoleFilter = RoleType | 'ALL'

export enum DominantHand {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

export enum SkillLevel {
  LEVEL_1 = 'LEVEL_1',
  LEVEL_2 = 'LEVEL_2',
  LEVEL_3 = 'LEVEL_3',
  LEVEL_4 = 'LEVEL_4',
  LEVEL_5 = 'LEVEL_5',
  LEVEL_6 = 'LEVEL_6',
  LEVEL_7_PLUS = 'LEVEL_7_PLUS',
}

export const SKILL_LEVEL_VALUES: Record<SkillLevel, string> = {
  [SkillLevel.LEVEL_1]: '1',
  [SkillLevel.LEVEL_2]: '2',
  [SkillLevel.LEVEL_3]: '3',
  [SkillLevel.LEVEL_4]: '4',
  [SkillLevel.LEVEL_5]: '5',
  [SkillLevel.LEVEL_6]: '6',
  [SkillLevel.LEVEL_7_PLUS]: '7+',
}

export type UpdatePasswordFormData = {
  currentPassword: FormField<string>
  newPassword: FormField<string>
}

export type ProfileDetailsModel = {
  firstName: string
  lastName: string
  nickname: string
  phoneNumber: string
  skillLevel: SkillLevel | null
  dominantHand: DominantHand | null
}

export type NotificationsModel = {
  emailNotifications: boolean
  pushNotifications: boolean
  marketingNotifications: boolean
}

export type PreferencesModel = {
  language: Language
  theme: ThemeType
}

export type HasUnreadNotifications = {
  hasUnread: boolean
}

export enum ProfileCompletionField {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  NICKNAME = 'nickname',
}
